import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Typography } from '@material-ui/core';
import { Icon } from '@saviynt/design-system';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchUserAttributes,
  fetchAllOIMConnections,
  fetchAllSailpointConnections,
  fetchSourceStatus,
} from '../../redux/migrationThunks';
import { ERROR_MESSAGES } from '../../constants/stringConstants';
import AttributeMappingContent from './AttributeMappingContent';
import ConnectionConfiguration from './ConnectionConfiguration';
import GlobalConfiguration from './GlobalConfiguration';
import useStyles from './styles/PrerequisitesModal.styles';

const TABS = {
  GLOBAL: 'global',
  MAPPING: 'mapping',
  DATA_MIGRATION: 'dataMigration',
  ROLE_MAPPING: 'roleMapping',
  ENTITLEMENT_MAPPING: 'entitlementMapping',
  ADVANCED_FILTERS: 'advancedFilters',
};

const NAV_ITEMS = [
  {
    id: TABS.GLOBAL,
    label: 'Configure connection',
    tabIcon: <Icon kind="Configure" size="small" />,
    subtitle: 'Connection & environment settings',
  },
  {
    id: TABS.DATA_MIGRATION,
    label: 'Global Configuration',
    tabIcon: <Icon kind="DataManagement" size="small" />,
    subtitle: 'Enable data migration settings',
  },
  {
    id: TABS.MAPPING,
    label: 'User Attribute Mapping',
    tabIcon: <Icon kind="UserManagement" size="small" />,
    subtitle: 'Map source to Saviynt attributes',
  },
];

function PrerequisitesModal({ isOpen, onClose, sourceID, sourceType, onConnectionSaved }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.GLOBAL);
  const [isClosing, setIsClosing] = useState(false);
  const {
    userAttributes,
    userAttributesLoading,
    saveMappingLoading,
    saveMappingError,
    oimConnectionOptions,
    sailpointConnectionOptions,
    connectionOptionsLoading,
    connectionDetails,
    saveConnectionError,
  } = useSelector((state) => state.migration ?? {});

  const apiError = saveConnectionError;

  useEffect(() => {
    if (!isOpen) return;
    if (sourceID) dispatch(fetchUserAttributes(sourceID));
    if (sourceType) {
      const fetchConnections = sourceType.toLowerCase().includes('oim')
        ? fetchAllOIMConnections
        : fetchAllSailpointConnections;
      dispatch(fetchConnections());
    }
  }, [isOpen, sourceID, sourceType, dispatch]);

  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 280);
  }, [onClose]);

  if (!isOpen && !isClosing) return null;

  const errorContent = apiError ? (
    <div className={classes.errorContainer}>
      <div className={classes.errorIconWrap}>
        <Icon kind="AlertCriticalOutline" color="--color-critical-700" label="Error" size="large" />
      </div>
      <div className={classes.errorText}>
        <Typography className={classes.errorTitle}>{ERROR_MESSAGES.BACKEND_UNAVAILABLE}</Typography>
        <Typography className={classes.errorMessage}>
          {ERROR_MESSAGES.CANNOT_CONNECT_BACKEND}
        </Typography>
      </div>
    </div>
  ) : null;

  const tabContent = (
    <>
      {activeTab === TABS.GLOBAL && (
        <ConnectionConfiguration
          sourceType={sourceType}
          classes={classes}
          oimConnectionOptions={oimConnectionOptions}
          sailpointConnectionOptions={sailpointConnectionOptions}
          connectionOptionsLoading={connectionOptionsLoading}
          connectionDetails={connectionDetails}
          onConnectionSaved={(connectionName) => {
            if (onConnectionSaved) onConnectionSaved(connectionName);
            if (sourceID) dispatch(fetchSourceStatus(sourceID));
          }}
        />
      )}
      {activeTab === TABS.MAPPING && (
        <AttributeMappingContent
          sourceID={sourceID}
          sourceType={sourceType}
          userAttributes={userAttributes}
          userAttributesLoading={userAttributesLoading}
          saveMappingLoading={saveMappingLoading}
          saveMappingError={saveMappingError}
          onSave={null}
        />
      )}
      {activeTab === TABS.DATA_MIGRATION && (
        <GlobalConfiguration classes={classes} sourceID={sourceID} />
      )}
    </>
  );

  return ReactDOM.createPortal(
    <div className={`${classes.overlay} ${isClosing ? classes.overlayClosing : ''}`}>
      <div className={`${classes.modal} ${isClosing ? classes.modalClosing : ''}`}>
        <div className={classes.rightPanel}>
          <div className={classes.header}>
            <div className={classes.headerLeft}>
              <div className={classes.headerSourceCircle}>
                {sourceType?.includes('oim') ? (
                  <img
                    src="/ECMv6/assets/images/oracle.png"
                    alt="oracle"
                    className={classes.headerSourceImg}
                  />
                ) : (
                  <img
                    src="/ECMv6/assets/images/sailpoint.png"
                    alt="sailpoint"
                    className={classes.headerSourceImg}
                  />
                )}
              </div>
              <div>
                <Typography className={classes.headerTitle}>Prerequisites</Typography>
                <Typography className={classes.headerSubtitle}>
                  Configure settings before starting migration
                </Typography>
              </div>
            </div>
            <div className={classes.headerRight}>
              <button
                type="button"
                className={classes.closeButton}
                onClick={handleClose}
                aria-label="Close"
              >
                <Icon kind="close" size="small" color="--color-neutral-000" />
              </button>
            </div>
          </div>
          <div className={classes.tabsBar}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`${classes.tab} ${activeTab === item.id ? classes.tabActive : ''}`}
                onClick={() => setActiveTab(item.id)}
              >
                <span className={classes.tabIcon}>{item.tabIcon}</span>
                {item.label}
              </button>
            ))}
          </div>
          <div className={classes.content}>{apiError ? errorContent : tabContent}</div>
        </div>
      </div>
    </div>,
    document.body
  );
}

PrerequisitesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sourceID: PropTypes.string,
  sourceType: PropTypes.string,
  onConnectionSaved: PropTypes.func,
};

PrerequisitesModal.defaultProps = {
  sourceID: null,
  sourceType: null,
  onConnectionSaved: null,
};

export default PrerequisitesModal;
